import { Injectable, NgZone } from '@angular/core';
import { createClient, SupabaseClient, Session, User, AuthError } from '@supabase/supabase-js';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { environment } from '@env/environment';

export interface AuthState {
  user: User | null;
  session: Session | null;
  loading: boolean;
  initialized: boolean;
}

@Injectable({ providedIn: 'root' })
export class AuthService {
  private supabase: SupabaseClient;

  private _loginRequest$ = new Subject<void>();
  readonly loginRequest$ = this._loginRequest$.asObservable();
  requestLogin(): void { this._loginRequest$.next(); }

  private _state$ = new BehaviorSubject<AuthState>({
    user: null,
    session: null,
    loading: true,
    initialized: false,
  });

  private _recoveryMode$ = new BehaviorSubject<boolean>(false);
  /** Emits true when Supabase fires a PASSWORD_RECOVERY event (reset link clicked). */
  readonly recoveryMode$ = this._recoveryMode$.asObservable();

  /**
   * True when this page load was initiated by clicking a Supabase password-reset link.
   * Captured before createClient() so we read the URL before Supabase clears it via
   * history.replaceState.
   */
  readonly isPasswordRecoveryLoad: boolean;

  readonly state$: Observable<AuthState> = this._state$.asObservable();

  get user(): User | null { return this._state$.value.user; }
  get session(): Session | null { return this._state$.value.session; }
  get isAuthenticated(): boolean { return !!this._state$.value.user; }
  get isLoading(): boolean { return this._state$.value.loading; }

  constructor(private zone: NgZone) {
    // Must read URL BEFORE createClient() - Supabase calls history.replaceState to strip
    // the hash fragment during client initialisation, so any check after createClient()
    // will always see an empty hash even on a genuine recovery-link page load.
    //
    // Supabase v2 defaults to PKCE flow: the recovery redirect arrives as
    //   /reset-password?code=<pkce_verifier>
    // NOT as /reset-password#type=recovery. So we also check for ?code= which indicates
    // a PKCE code-exchange redirect that will fire PASSWORD_RECOVERY via onAuthStateChange.
    this.isPasswordRecoveryLoad =
      window.location.hash.includes('type=recovery') ||
      window.location.search.includes('type=recovery') ||
      (window.location.pathname.includes('reset-password') && window.location.search.includes('code='));

    this.supabase = createClient(environment.supabaseUrl, environment.supabaseAnonKey, {
      auth: {
        autoRefreshToken: true,
        persistSession: true,
        detectSessionInUrl: true,
        storageKey: `pxlvd-auth-${environment.supabaseUrl.split('.')[0].split('//')[1]}`,
      }
    });

    // Patch the internal lock mechanism directly - the 'lock' option in createClient
    // is not honoured in this version of @supabase/auth-js. We replace the lock
    // function on the GoTrue client instance after creation to prevent
    // NavigatorLockAcquireTimeoutError when multiple tabs or hard reloads occur.
    try {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const gotrue = (this.supabase as any).auth;
      if (gotrue && typeof gotrue['_acquireLock'] === 'function') {
        gotrue['_acquireLock'] = <R>(_timeout: number, fn: () => Promise<R>) => fn();
      }
      if (gotrue && gotrue['_storage'] && typeof gotrue['_storage']['lock'] === 'function') {
        gotrue['_storage']['lock'] = <R>(_name: string, _timeout: number, fn: () => Promise<R>) => fn();
      }
    } catch { /* ignore if internal API changes */ }

    this.supabase.auth.onAuthStateChange((event, session) => {
      this.zone.run(() => {
        if (event === 'PASSWORD_RECOVERY') {
          this._recoveryMode$.next(true);
        } else if (event === 'SIGNED_OUT') {
          // Do NOT clear on SIGNED_IN - Supabase fires SIGNED_IN immediately after
          // PASSWORD_RECOVERY as it establishes the recovery session, which would
          // flip recoveryMode$ back to false before the component reads it.
          this._recoveryMode$.next(false);
        }
        this._state$.next({
          user: session?.user ?? null,
          session,
          loading: false,
          initialized: true,
        });
      });
    });

    this.supabase.auth.getSession().then(({ data }) => {
      this.zone.run(() => {
        this._state$.next({
          user: data.session?.user ?? null,
          session: data.session ?? null,
          loading: false,
          initialized: true,
        });
      });
    }).catch(() => {
      this.zone.run(() => {
        this._state$.next({
          user: null, session: null, loading: false, initialized: true,
        });
      });
    });
  }

  async signUpWithEmail(email: string, password: string): Promise<{ error: AuthError | null }> {
    const { error } = await this.supabase.auth.signUp({
      email,
      password,
      options: { emailRedirectTo: `${window.location.origin}` }
    });
    return { error };
  }

  async signInWithEmail(email: string, password: string): Promise<{ error: AuthError | null }> {
    const { error } = await this.supabase.auth.signInWithPassword({ email, password });
    return { error };
  }

  async signInWithGoogle(): Promise<{ error: AuthError | null }> {
    const { error } = await this.supabase.auth.signInWithOAuth({
      provider: 'google',
      options: {
        redirectTo: window.location.href,
        queryParams: {
          prompt: 'select_account',
          access_type: 'online',
        },
      }
    });
    return { error };
  }

  async signOut(): Promise<void> {
    await this.supabase.auth.signOut({ scope: 'local' });
    const storageKey = `pxlvd-auth-${environment.supabaseUrl.split('.')[0].split('//')[1]}`;
    localStorage.removeItem(storageKey);
    sessionStorage.clear();
  }

  async resetPassword(email: string): Promise<{ error: AuthError | null }> {
    const { error } = await this.supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    return { error };
  }

  async updatePassword(password: string): Promise<{ error: AuthError | null }> {
    const { error } = await this.supabase.auth.updateUser({ password });
    if (!error) this._recoveryMode$.next(false);
    return { error };
  }

  async getAccessToken(): Promise<string | null> {
    const { data } = await this.supabase.auth.getSession();
    return data.session?.access_token ?? null;
  }

  getDisplayName(): string {
    const user = this.user;
    if (!user) return '';
    return user.user_metadata?.['full_name']
      ?? user.user_metadata?.['name']
      ?? user.email?.split('@')[0]
      ?? 'User';
  }

  getAvatarUrl(): string | null {
    return this.user?.user_metadata?.['avatar_url'] ?? null;
  }

  // ── Profile (profiles table) ──────────────────────────────────

  async getProfile(): Promise<{ username: string | null; avatar: string } | null> {
    if (!this.user) return null;
    const { data } = await (this.supabase as any)
      .from('profiles')
      .select('username, avatar')
      .eq('id', this.user.id)
      .single();
    return data ?? null;
  }

  async checkUsername(username: string): Promise<boolean> {
    const { data } = await (this.supabase as any)
      .from('profiles')
      .select('username')
      .eq('username', username.toLowerCase())
      .maybeSingle();
    return !data; // true = available
  }

  async updateUsername(username: string): Promise<{ error: any }> {
    if (!this.user) return { error: new Error('Not authenticated') };
    const { error } = await (this.supabase as any)
      .from('profiles')
      .update({ username: username.toLowerCase() })
      .eq('id', this.user.id);
    return { error };
  }

  async hasUsername(): Promise<boolean> {
    const profile = await this.getProfile();
    return !!profile?.username;
  }

  /** Returns true if the user has an active (non-refunded) completed payment. */
  async hasPaid(): Promise<boolean> {
    if (!this.user) return false;
    const { data: completed } = await (this.supabase as any)
      .from('payments')
      .select('id')
      .eq('user_id', this.user.id)
      .eq('status', 'completed')
      .limit(1);
    if (!completed?.length) return false;

    // Check no active refund/dispute exists
    const { data: revoked } = await (this.supabase as any)
      .from('payments')
      .select('id')
      .eq('user_id', this.user.id)
      .in('status', ['refunded', 'disputed'])
      .limit(1);
    return !revoked?.length;
  }
}
